import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Tooltip } from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { CalloutContext } from '@folio/stripes/core';
import { MdBookmark, MdBookmarkBorder } from 'react-icons/md';
import useFavoriteReportsAPI from '../../hooks/useFavoriteReportsAPI';

function FavoriteButton({ reportId, iconOnly = false, asDropdownItem = false }) {
  const { isFavorite, getFavoriteId, addFavorite, removeFavorite } = useFavoriteReportsAPI();
  const intl = useIntl();
  const callout = useContext(CalloutContext);
  const isFav = isFavorite(reportId);

  const handleToggle = () => {
    if (isFav) {
      const favoriteId = getFavoriteId(reportId);
      removeFavorite.mutate(favoriteId, {
        onSuccess: () => {
          callout?.sendCallout({
            type: 'success',
            message: intl.formatMessage({
              id: 'ui-reports.favorites.removeFromFavorites.success',
              defaultMessage: 'Report removed from favorites',
            }),
          });
        },
      });
    } else {
      addFavorite.mutate({ reportId }, {
        onSuccess: () => {
          callout?.sendCallout({
            type: 'success',
            message: intl.formatMessage({
              id: 'ui-reports.favorites.addToFavorites.success',
              defaultMessage: 'Report added to favorites',
            }),
          });
        },
      });
    }
  };

  const FavIcon = isFav ? MdBookmark : MdBookmarkBorder;
  const iconColor = isFav ? '#f0ab00' : 'inherit';

  const label = isFav
    ? <FormattedMessage id="ui-reports.favorites.removeFromFavorites" defaultMessage="Remove from favorites" />
    : <FormattedMessage id="ui-reports.favorites.addToFavorites" defaultMessage="Add to favorites" />;

  if (asDropdownItem) {
    return (
      <Button
        marginBottom0
        id={`favorite-btn-dropdown-${reportId}`}
        buttonStyle="dropdownItem"
        onClick={handleToggle}
        onMouseDown={(e) => e.preventDefault()}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FavIcon size={18} color={iconColor} />
          {label}
        </span>
      </Button>
    );
  }

  if (iconOnly) {
    return (
      <Tooltip
        id={`favorite-btn-tooltip-${reportId}`}
        text={label}
      >
        {({ ref, ariaIds }) => (
          <Button
            ref={ref}
            aria-labelledby={ariaIds.text}
            buttonStyle="none"
            onClick={handleToggle}
            marginBottom0
          >
            <FavIcon size={20} color={iconColor} />
          </Button>
        )}
      </Tooltip>
    );
  }

  return (
    <Button buttonStyle="default" onClick={handleToggle}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <FavIcon size={18} color={iconColor} />
        {label}
      </span>
    </Button>
  );
}

FavoriteButton.propTypes = {
  reportId: PropTypes.string.isRequired,
  iconOnly: PropTypes.bool,
  asDropdownItem: PropTypes.bool,
};

export default FavoriteButton;
